import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { catchError, EMPTY } from 'rxjs';
import {
	AvailabilityRoomStatus,
	GeneralRoomAvailability,
	OfferTerm,
	ProductRoomAvailability,
	ProductRoomsAvailabilities,
	RoomAvailability,
} from '../bag-transport-term.model';
import { BagTransportTermChangeMaxRoomsComponent } from './bag-transport-term-change-max-rooms/bag-transport-term-change-max-rooms.component';
import { BagTransportTermChangeRoomAvailabilityApiService } from './bag-transport-term-change-room-availability-api.service';
import { BagTransportTermGeneralRoomsDataComponent } from './bag-transport-term-general-rooms-data/bag-transport-term-general-rooms-data.component';
import { BagTransportTermRoomAvailabilitiesComponent } from './bag-transport-term-room-availabilities/bag-transport-term-room-availabilities.component';

@Component({
	selector: 'app-bag-transport-term-change-room-availability',
	standalone: true,
	imports: [
		MatProgressBarModule,
		BagTransportTermChangeMaxRoomsComponent,
		BagTransportTermGeneralRoomsDataComponent,
		BagTransportTermRoomAvailabilitiesComponent,
	],
	templateUrl: './bag-transport-term-change-room-availability.component.html',
	styleUrl: './bag-transport-term-change-room-availability.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagTransportTermChangeRoomAvailabilityComponent implements OnInit {
	termId = input.required<number>();
	productId = input.required<number>();

	rooms = signal<RoomAvailability[]>([]);
	loadingData = signal<boolean>(true);
	maxRooms = signal<number>(0);

	generalRoomsData = signal<GeneralRoomAvailability>(null);

	private readonly destroyRef = inject(DestroyRef);
	private readonly bagTransportTermChangeRoomAvailabilityApiService = inject(
		BagTransportTermChangeRoomAvailabilityApiService,
	);

	ngOnInit(): void {
		this.loadProductMaxRooms();
		this.loadProductAvailableRooms();
	}

	editRoomAvailability(room: RoomAvailability): void {
		const updatedRooms = this.rooms().map((single) => ({
			...single,
			mode: single.id === room.id ? AvailabilityRoomStatus.SAVE : AvailabilityRoomStatus.DISABLED,
		}));
		this.rooms.set(updatedRooms);
	}

	changeRoomAvailability(dataToSend: ProductRoomAvailability): void {
		this.loadingData.set(true);
		this.bagTransportTermChangeRoomAvailabilityApiService
			.changeProductAvailableRooms(this.productId(), dataToSend)
			.pipe(
				catchError(() => {
					this.loadingData.set(false);
					this.loadProductAvailableRooms();
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.loadProductAvailableRooms();
			});
	}

	cancelRoomAvailability(): void {
		this.prepareInitialStateAtRooms(this.rooms());
	}

	refreshRooms(): void {
		this.loadProductAvailableRooms();
	}

	refreshMaxRooms(): void {
		this.loadProductMaxRooms();
	}

	private loadProductAvailableRooms(): void {
		this.bagTransportTermChangeRoomAvailabilityApiService
			.productAvailableRooms(this.productId(), this.termId())
			.pipe(
				catchError(() => {
					this.loadingData.set(false);
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((result: ProductRoomsAvailabilities) => {
				this.prepareGeneralRoomsAvailability(result.sumOfConfirmedOffers, result.sumOfOptionOffers);
				this.prepareInitialStateAtRooms(result.roomTypes);
				this.loadingData.set(false);
			});
	}

	private loadProductMaxRooms(): void {
		this.bagTransportTermChangeRoomAvailabilityApiService
			.productMaxRooms(this.termId())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: OfferTerm) => {
				this.maxRooms.set(result.maxRooms ?? 0);
			});
	}

	private prepareInitialStateAtRooms(rooms: RoomAvailability[]): void {
		const updatedRooms = rooms.map((single) => ({
			...single,
			mode: AvailabilityRoomStatus.EDIT,
		}));
		this.rooms.set(updatedRooms);
	}

	private prepareGeneralRoomsAvailability(sumOfConfirmedOffers: number, sumOfOptionOffers: number): void {
		this.generalRoomsData.set({
			sumOfOptionOffers: sumOfOptionOffers,
			sumOfConfirmedOffers: sumOfConfirmedOffers,
			sumOfAllReservations: sumOfOptionOffers + sumOfConfirmedOffers,
		});
	}
}
